import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAward, FiUsers, FiHeart, FiTrendingUp, FiMapPin, FiPhone, FiMail } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiHeart,
      title: 'Customer First',
      description: 'We put our customers at the center of everything we do, ensuring exceptional service and satisfaction.',
    },
    {
      icon: FiAward,
      title: 'Quality Assurance',
      description: 'Every vehicle undergoes rigorous inspection to meet our high standards for quality and reliability.',
    },
    {
      icon: FiUsers,
      title: 'Expert Team',
      description: 'Our experienced professionals are dedicated to helping you find the perfect vehicle for your needs.',
    },
    {
      icon: FiTrendingUp,
      title: 'Continuous Growth',
      description: 'We continuously evolve and improve our services to better serve our community.',
    },
  ];

  const team = [
    {
      name: 'Wayne Wilder',
      position: 'Owner, General Manager',
      image: '/assets/assets/images/team/wayne.webp',
      description: 'Founder of Paradise-Performance LLC with expertise in quality vehicle sales and dealership management.',
    },
    {
      name: 'Robin Wilder',
      position: 'Vice President',
      image: '/assets/assets/images/team/robin.webp',
      description: 'Oversees dealership operations and ensures exceptional customer experience across all departments.',
    },
    {
      name: 'David Barrera',
      position: 'Sales Manager',
      image: '/assets/assets/images/team/david.webp',
      description: 'University of Texas Austin graduate bringing expertise in sales management and customer relations to help you find the perfect vehicle.',
    },
    {
      name: 'Colton Wilder',
      position: 'Lead Mechanic & Performance-Racing Technician',
      image: '/assets/assets/images/team/colton.webp',
      description: 'Expert in European car repair and diesel performance modifications with specialized training.',
    },
    {
      name: 'Chloe Wilder',
      position: 'Director of Marketing',
      image:'/assets/assets/images/team/chloe.webp',
      description: 'Manages all marketing initiatives and customer outreach programs for Paradise-Performance LLC.',
    }
  ];

  return (

    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50"
    >
      <Helmet>
        <title>About Paradise-Performance LLC | Family-Owned Faith Based Automotive Hub</title>
        <meta name="description" content="Meet our team — a faith-based, family-owned auto dealer in Paradise, TX. Serving DFW with affordable pricing, honest work and quality services since day one." />
        <link rel="canonical" href="https://paradise-performance.com/about" />
        <meta property="og:title" content="About Paradise-Performance LLC | Family-Owned & Faith-Based Automotive Hub" />
        <meta property="og:description" content="Trusted auto dealer serving Paradise, TX with quality vehicles and honest service." />
        <meta property="og:url" content="https://paradise-performance.com/about" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About Paradise-Performance LLC",
            "url": "https://paradise-performance.com/about",
            "mainEntity": {
              "@type": "AutoDealer",
              "name": "Paradise-Performance LLC",
              "foundingDate": "2023",
              "description": "Family-owned auto dealership specializing in quality used vehicles",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "305 Co Rd 3470",
                "addressLocality": "Paradise",
                "addressRegion": "TX",
                "postalCode": "76073"
              }
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-900 to-primary-700 text-white py-20 pt-24 lg:pt-36 md:pt-16 sm:pt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              The Journey to Paradise-Performance
            </motion.h1>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-gray-200"
            >
              A faith-based family business committed to integrity, quality service, 
              and building lasting relationships with our customers.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <motion.section
      initial={{ opacity: 0 }}
      
      animate={{ opacity: 1 }}
      transition={{ delay: 2.2 }}
      >
        <div className="container mx-auto px-4">
          <div className="w-full mx-auto">
            <div className="grid grid-cols-1 py-32 pb-10 lg:grid-cols-2 gap-12 items-left">
              <div>
                <motion.h2
                  initial={{ y: 30, opacity: 0.5 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="text-3xl font-bold text-gray-900 mb-6"
                >
                  Routine Auto Maintenance
                </motion.h2>
                <motion.div
                  initial={{ y: 60, opacity: 1 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.02 }}
                  className="space-y-4 text-gray-600"
                >
                  <p>
                    Wayne Wilder founded Paradise-Performance after 27 years in corporate sales, asking "what is important to my family and me?" The business grew from a father-son hobby to a full-service dealership and repair shop focused on integrity and quality.
                  </p>
                  <p>
                    Our approach is simple: we sell cars and put our own families in. No salvage titles, no hidden problems, no pressure tactics. Each vehicle comes with a complete inspection report and service history. If something needs attention, we fix it before it hits the lot or tell you exactly what to expect.
                  </p>
                  <p>
                    Building trust isn't just good business – it's the right thing to do. Whether you need reliable transportation for work or a safe vehicle for your teenager, we'll help you find the right fit for your budget and needs. And unlike those big dealerships, we'll still wave when we see you around town.
                  </p>
                </motion.div>
              </div>
              <motion.div
                initial={{ x: 30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="relative"
              >
                <img
                  src="https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?q=80&w=1074&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  alt="Paradise-Performance LLC Dealership"
                  className="rounded-lg shadow-lg"
                />
                <div className="absolute -bottom-4 -right-4 bg-primary-600 text-white p-4 rounded-lg">
                  <div className="text-2xl font-bold">⭐️⭐️⭐️⭐️⭐️</div>
                  <div className="text-sm">Best Rated DFW</div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Our Values */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Faith-Based Used Car Dealership</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              At Paradise-Performance LLC, we are a faith-based family owned business dedicated to providing high-quality used vehicles, European & domestic repair serving Dallas, Denton & Fort Worth (or from anywhere else) drivers - Stop by today! We're not your typical used car lot.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Operating Our Business With Integrity</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our business is founded on faith and family values. We operate with the understanding that integrity matters in every transaction and service we provide.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="space-y-4 text-gray-600"
              >
                <p>
                  "My wife Robin has always stood by me. Apart from Jesus, she's my rock. We share everything when it comes to our faith, our ideas, and the type of life we want for our family." - Wayne Wilder
                </p>
                <p>
                  Our business runs on a simple principle: treat others the way we'd want to be treated. Sounds basic, but you'd be surprised how rare that is in the auto industry. My wife Robin and I didn't set out to revolutionize car repair. We just wanted to create the kind of shop we'd been looking for ourselves - one where you're not just another invoice number, where someone actually listens to that weird noise your car's been making, and where "I don't know, let me find out" is an acceptable answer.
                </p>
                <p>
                  Every morning, our team starts with the same commitment: do right by our customers, even when nobody's watching. That means calling you if we find something unexpected instead of just fixing it and surprising you with the bill. It means suggesting the repair that makes sense for your situation, not just the one with the highest margin. Sometimes it even means telling you to wait on a repair if it's not critical yet.
                </p>
              </motion.div>
              <motion.div
                initial={{ x: 30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="relative"
              >
                <img
                  src="https://paradise-performance.com/wp-content/uploads/2025/04/european_auto_repair_shop_in_dallas_paradise-Performance.webp"
                  alt="Paradise-Performance LLC Sign"
                  className="rounded-lg shadow-lg"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our family team is committed to providing you with honest service and finding the right vehicle for your needs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-48 object-contain bg-gray-100"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
                  <div className="text-primary-600 font-medium mb-3">{member.position}</div>
                  <p className="text-gray-600 text-sm">{member.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment to Excellence */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="relative order-2 lg:order-1"
            >
              <div className="bg-red-50 rounded-lg p-8 shadow-lg">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Commitment To Excellence</h2>
                <div className="space-y-4 text-gray-600">
                  <p>
                    We're serious about having the industry's best service record. Unlike big dealers, we don't have a huge advertising budget. For us, it's about the details nobody sees - the extra time spent diagnosing an intermittent problem, the care we take with your car's running smoothly, the willingness to explain what we're doing and why.
                  </p>
                  <p>
                    What sets us apart is consistency. Whether you're here for a simple oil change or a complex diesel repair, you get the same level of attention and expertise. We invest in ongoing training but avoid vehicles losing more sophistication. We maintain relationships with parts suppliers because our commitment is to quality. Most importantly, we remember that behind every car is a person counting on us to keep them safe and mobile.
                  </p>
                  <p>
                    We believe in building behind our work which is why we offer solid warranties and actually honor the simple things you ask. It's an ordinary process for decades now, but when you combine our small town values, old-fashioned integrity, and modern expertise makes a pretty good combination.
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="order-1 lg:order-2"
            >   
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Experience the Paradise-Performance Difference?</h2>
          <p className="text-xl mb-8 text-primary-200">
            Visit us today and discover why families throughout North Texas have trusted us with their automotive needs for over 1 years.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <a
              href="/inventory"
              className="bg-white text-primary-900 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
            >
              Visit Our Showroom
            </a>
            <a
              href="tel:+18173822316"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-primary-900 transition-colors"
            >
              Call Us Today
            </a>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;